'use client'
import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import CtaSimple from "@/components/CtaSimple";
import { PrismicNextImage } from "@prismicio/next";


/**
 * @typedef {import("@prismicio/client").Content.MapBoxSlice} MapBoxSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<MapBoxSlice>} MapBoxProps
 * @type {import("react").FC<MapBoxProps>}
 */
const MapBox = ({ slice }) => {
  const { title, text, informations, cta, maps, equipments, title_equipment } = slice.primary;

  const mapContainer = useRef(null);

  // Récupère le premier point pour centrer la carte, sinon fallback sur Paris
  const firstMap = slice.primary.maps?.[0];
  const centerLng = firstMap?.geolocation?.longitude ?? 2.3522;
  const centerLat = firstMap?.geolocation?.latitude ?? 48.8566;
  const zoom = 13;

  useEffect(() => {
    mapboxgl.accessToken = "pk.eyJ1IjoieWVzeWVzcGFkZWwiLCJhIjoiY21mY3F5N3k2MDFhdDJtczMyNTVhb2swbSJ9.W7qduyKmwajnJcWq8JNX0A";
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [centerLng, centerLat],
      zoom: zoom,
    });

    slice.primary.maps?.forEach(item => {
      const lng = item.geolocation?.longitude;
      const lat = item.geolocation?.latitude;
      if (lng && lat) {
        const el = document.createElement('div');
        el.style.backgroundImage = 'url(/lieu-yyp.svg)';
        el.style.width = '36px';
        el.style.height = '45px';
        el.style.backgroundSize = '100%';

        const marker = new mapboxgl.Marker(el)
          .setLngLat([lng, lat]);
        if (item.label) {
          marker.setPopup(
            new mapboxgl.Popup().setHTML(`
              <div style="padding: 8px; min-width: 250px; color:#003a33">
                <img src="/yy-green.svg" style="width:50px; margin-bottom: 8px;" />
                <p style="margin-bottom:20px;">${item.label}</p>
                <a class="bouton"
                  href="https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}" 
                  target="_blank" 
                >
                  S'y rendre
                </a>
              </div>
            `)
          );
        }
        marker.addTo(map);
      }
    });

    return () => map.remove();
  }, [centerLng, centerLat, zoom, slice.primary.maps]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="my-20 text-dark-green framing"
    >
      <div className="max-w-375 mx-auto w-full flex flex-col lg:flex-row gap-10 md:gap-20 xl:gap-30 2xl:gap-40">
        <div className="lg:w-1/2 flex flex-col gap-15">
          <div className="flex flex-col gap-6">
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="h3">
                      {children}
                    </h2>
                  ),
                  heading3: ({ children }) => (
                    <h3 className="h3">
                      {children}
                    </h3>
                  ),
                }}
              />
            )}
            {isFilled.richText(text) && (
              <PrismicRichText
                field={text}
                components={{
                  paragraph: ({ children }) => (
                    <p>
                      {children}
                    </p>
                  ),
                }}
              />
            )}
          </div>
          <div className="flex flex-col gap-5">
            {isFilled.group(informations) && (
              <div className="flex flex-col gap-5">
                {informations.map((item, idx) => (
                  <div key={idx} className="flex flex-col gap-1 pb-5 border-b-1 border-dark-green last:border-0">
                    <PrismicRichText
                      field={item.information}
                      components={{
                        heading3: ({ children }) => (
                          <h3 className="h3-sm uppercase">
                            {children}
                          </h3>
                        ),
                        heading4: ({ children }) => (
                          <h4 className="h3-sm uppercase">
                            {children}
                          </h4>
                        ),
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {isFilled.link(cta) && (
              <CtaSimple page={cta} padding={"px-10"} width={"w-full md:w-fit"} hover={"dark-green"} />
            )}
          </div>
        </div>
        <div className="lg:w-1/2 flex flex-col justify-between">
          <div className="lg:flex-1 mb-10 h-75 md:h-100 lg:h-auto">
            {isFilled.group(maps) && (
              <div
                ref={mapContainer}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "16px", 
                  overflow: "hidden"   
                }}
              />
            )}
          </div>
          {isFilled.group(equipments) && (
            <div className="flex flex-col gap-6">
              {isFilled.richText(title_equipment) && (
                <PrismicRichText
                  field={title_equipment}
                  components={{
                    heading3: ({ children }) => (
                      <h3 className="h3-sm">
                        {children}
                      </h3>
                    ),
                    heading4: ({ children }) => (
                      <h4 className="h3-sm">
                        {children}
                      </h4>
                    ),
                  }}
                />
              )}
              <div className="grid grid-cols-2 gap-7">
                {equipments.map((item, idx) => (
                  <div key={idx} className="flex gap-4 items-center">
                    {isFilled.image(item.picto) && (
                      <PrismicNextImage field={item.picto} className="h-auto w-[50px] object-contain" />
                    )}
                    {isFilled.richText(item.text) && (
                      <PrismicRichText
                        field={item.text}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="p-sm">
                              {children}
                            </p>
                          ),
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

    </section>
  );
};

export default MapBox;
